import { useEffect, useRef, useState } from "react";
import Select, { components } from "react-select";
import FieldError from "./FieldError";
import CompanySection from "./CompanySection";
import Tooltip from "../../utils/tooltip";
import countryPrefixData from "../../data/prefijo-paises.json";

const CustomDropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <path
        d="M6 8l4 4 4-4"
        fill="none"
        stroke="var(--textSecondary)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </components.DropdownIndicator>
);

function ContactInfoSection({
  language,
  USA_MODE,
  phone,
  setPhone,
  position,
  setPosition,
  region,
  setRegion,
  sector,
  setSector,
  employees,
  setEmployees,
  setStoredValue,
  formErrors,
  paisesRegiones,
  sectorCompany,
  company,
  companyOptions,
  handleCompanyChange,
  createCompany,
  businessGroup,
  setBusinessGroup,
  mainRole,
  setMainRole,
  mainRoleOther,
  setMainRoleOther,
  mainInterest,
  setMainInterest,
  mainInterestOther,
  setMainInterestOther,
  eventSource,
  setEventSource,
  eventSourceOther,
  setEventSourceOther,
}) {
  const t = {
    EN: {
      phone: "Phone",
      phoneTitle: "Enter a valid phone number.",
      phoneTooltip:
        "In the dropdown, search for and select your country code. Then complete the field with your area code and phone number.",
      jobTitle: "Job title",
      state: "State",
      province: "Country - State",
      selectState: "Select a state",
      selectProvince: "Select a Country - State",
      industry: "Industry",
      selectIndustry: "Select an industry",
      companySize: "Company size",
      selectRange: "Select a range",
      moreThan: "More than 1,000",
      businessGroup:
        "Is your company part of a business group, chamber, association, or sector entity?",
      mainRole: "What is your main role?",
      mainInterest: "What is your main interest in the event?",
      eventSource: "How did you hear about the event?",
      specifyOther: "Tell us which one",
      otherRequired: "Please specify the selected option.",
      selectOption: "Select an option",
      selectMultiple: "Select one or more options",
      noOptions: "No options",
      businessGroupOptions: ["Yes", "No", "I don't know"],
      mainRoleOptions: [
        "Management / C-level",
        "Management",
        "Technical / Specialist",
        "Commercial / Business development",
        "Consultant / Advisor",
        "Other",
      ],
      mainInterestOptions: [
        "Networking",
        "Technical content",
        "Industry trends",
        "Commercial opportunities",
        "Other",
      ],
      eventSourceOptions: [
        "EconoJournal website",
        "EconoJournal newsletter",
        "EconoJournal social media",
        "LinkedIn ads",
        "Google ads",
        "WhatsApp",
        "Invitation from my company",
        "Recommendation from a colleague or contact",
        "Chamber, association, or sector institution",
        "I had already participated in another EconoJournal event",
        "Other [Specify]",
      ],
    },
    ES: {
      phone: "Teléfono",
      phoneTitle: "Ingresá un número de teléfono válido.",
      phoneTooltip:
        "En el desplegable, buscá y seleccioná el código de tu país. Luego completá el campo con tu código de área y número telefónico.",
      jobTitle: "Cargo",
      state: "Estado",
      province: "País - Provincia",
      selectState: "Seleccioná un estado",
      selectProvince: "Seleccioná una opción",
      industry: "¿En qué segmento de la industria trabajás?",
      selectIndustry: "Seleccioná un rubro",
      companySize: "¿Cuántas personas trabajan en tu empresa?",
      selectRange: "Seleccioná una cantidad",
      moreThan: "Más de 1.000",
      businessGroup:
        "¿Tu empresa forma parte de algún grupo empresario, cámara, asociación, entidad sectorial?",
      mainRole: "¿Cuál es tu rol principal?",
      mainInterest: "¿Qué te interesa principalmente del evento?",
      eventSource: "¿Cómo te enteraste del evento?",
      specifyOther: "Contanos cuál",
      otherRequired: "Especificá la opción seleccionada.",
      selectOption: "Seleccioná una opción",
      selectMultiple: "Seleccioná una o más opciones",
      noOptions: "Sin opciones",
      businessGroupOptions: ["Sí", "No", "No lo sé"],
      mainRoleOptions: [
        "Dirección / C-level",
        "Gerencia",
        "Técnico / Especialista",
        "Comercial / Desarrollo de negocio",
        "Consultor / Asesor",
        "Otro",
      ],
      mainInterestOptions: [
        "Networking",
        "Contenido técnico",
        "Tendencias del sector",
        "Oportunidades comerciales",
        "Otro",
      ],
      eventSourceOptions: [
        "Sitio web de EconoJournal",
        "Newsletter de EconoJournal",
        "Redes sociales de EconoJournal",
        "Publicidad en LinkedIn",
        "Publicidad en Google",
        "WhatsApp",
        "Invitación de mi empresa",
        "Recomendación de un colega o contacto",
        "Cámara, asociación o institución del sector",
        "Ya había participado en otro evento de EconoJournal",
        "Otro [Especificar]",
      ],
    },
  };

  const usaStates = [
    "Alabama",
    "Alaska",
    "Arizona",
    "Arkansas",
    "California",
    "Colorado",
    "Connecticut",
    "Delaware",
    "Florida",
    "Georgia",
    "Hawaii",
    "Idaho",
    "Illinois",
    "Indiana",
    "Iowa",
    "Kansas",
    "Kentucky",
    "Louisiana",
    "Maine",
    "Maryland",
    "Massachusetts",
    "Michigan",
    "Minnesota",
    "Mississippi",
    "Missouri",
    "Montana",
    "Nebraska",
    "Nevada",
    "New Hampshire",
    "New Jersey",
    "New Mexico",
    "New York",
    "North Carolina",
    "North Dakota",
    "Ohio",
    "Oklahoma",
    "Oregon",
    "Pennsylvania",
    "Rhode Island",
    "South Carolina",
    "South Dakota",
    "Tennessee",
    "Texas",
    "Utah",
    "Vermont",
    "Virginia",
    "Washington",
    "West Virginia",
    "Wisconsin",
    "Wyoming",
  ];

  const [countries, setCountries] = useState([]);
  const [countryCode, setCountryCode] = useState("+54");
  const [countryIso, setCountryIso] = useState("AR");

  const normalizedOnMountRef = useRef(false);

  const handleCountryChange = (e) => {
    const selectedIso = e.target.value;
    const selectedCountry = countries.find((c) => c.iso === selectedIso);

    if (!selectedCountry) return;

    setCountryIso(selectedCountry.iso);
    setCountryCode(selectedCountry.code);

    const localPhone = String(phone || "").replace(/\D/g, "");
    setStoredValue("phone", localPhone ? `${selectedCountry.code}${localPhone}` : "");
  };

  const handlePhoneChange = (e) => {
    const cleaned = e.target.value.replace(/\D/g, "");
    setPhone(cleaned);
    setStoredValue("phone", cleaned ? `${countryCode}${cleaned}` : "");
  };

  const handleQuestionChange = (field, value, setter) => {
    setter(value);
    setStoredValue(field, value);
  };

  const isOtherValue = (value) => value === "Other" || value === "Otro" || value === "Other [Specify]" || value === "Otro [Especificar]";

  const toOptions = (options) =>
    options.map((option) => ({
      label: option,
      value: option,
    }));

  const parseMultiValue = (value) => {
    if (Array.isArray(value)) return value;
    if (!value) return [];

    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [value];
    }
  };

  const handleMultiQuestionChange = (field, selectedOptions, setter) => {
    const values = (selectedOptions || []).map((option) => option.value);
    const serialized = values.length ? JSON.stringify(values) : "";

    setter(serialized);
    setStoredValue(field, serialized);
  };

  useEffect(() => {
    const formatted = countryPrefixData
      .map((country) => ({
        name: country.nombre || "",
        code: country.prefijo || "",
        iso: country.iso2 || "",
      }))
      .filter((country) => country.name && country.code && country.iso)
      .sort((a, b) => a.name.localeCompare(b.name));

    setCountries(formatted);

    const defaultCountry = USA_MODE
      ? formatted.find((c) => c.iso === "US")
      : formatted.find((c) => c.iso === "AR");

    if (defaultCountry) {
      setCountryCode(defaultCountry.code);
      setCountryIso(defaultCountry.iso);
    }
  }, [USA_MODE]);

  useEffect(() => {
    if (!countries.length) return;
    if (normalizedOnMountRef.current) return;

    const incomingPhone = String(phone || "").trim();

    if (!incomingPhone) {
      normalizedOnMountRef.current = true;
      return;
    }

    if (incomingPhone.startsWith("+")) {
      const sortedCountries = [...countries].sort(
        (a, b) => b.code.length - a.code.length
      );

      const matchedCountry = sortedCountries.find((c) =>
        incomingPhone.startsWith(c.code)
      );

      if (matchedCountry) {
        const localPhone = incomingPhone
          .slice(matchedCountry.code.length)
          .replace(/\D/g, "");

        setCountryIso(matchedCountry.iso);
        setCountryCode(matchedCountry.code);
        setPhone(localPhone);
        setStoredValue(
          "phone",
          localPhone ? `${matchedCountry.code}${localPhone}` : ""
        );
      } else {
        const cleaned = incomingPhone.replace(/\D/g, "");
        setPhone(cleaned);
      }

      normalizedOnMountRef.current = true;
      return;
    }

    const defaultCountry = USA_MODE
      ? countries.find((c) => c.iso === "US")
      : countries.find((c) => c.iso === "AR");

    if (defaultCountry) {
      const numericCountryCode = defaultCountry.code.replace(/\D/g, "");

      if (
        incomingPhone.startsWith(numericCountryCode) &&
        incomingPhone.length > numericCountryCode.length + 5
      ) {
        const localPhone = incomingPhone.slice(numericCountryCode.length);

        setCountryIso(defaultCountry.iso);
        setCountryCode(defaultCountry.code);
        setPhone(localPhone.replace(/\D/g, ""));
        setStoredValue(
          "phone",
          localPhone ? `${defaultCountry.code}${localPhone.replace(/\D/g, "")}` : ""
        );

        normalizedOnMountRef.current = true;
        return;
      }
    }

    setPhone(incomingPhone.replace(/\D/g, ""));
    normalizedOnMountRef.current = true;
  }, [countries, phone, setPhone, setStoredValue, USA_MODE]);

  const multiSelectStyles = {
    control: (base) => ({
      ...base,
      alignItems: "center",
      border: "1px solid var(--borderColor)",
      background: "var(--bgSurface)",
      borderRadius: "5px",
      boxSizing: "border-box",
      boxShadow: "none",
      color: "var(--textSecondary)",
      fontSize: "14px",
      height: "37px",
      marginBottom: "8px",
      minHeight: "37px",
      paddingLeft: "10px",
      width: "calc(100% - 10px)",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: "35px",
    }),
    valueContainer: (base) => ({
      ...base,
      gap: "6px",
      height: "35px",
      overflow: "hidden",
      padding: "0 8px 0 0",
    }),
    placeholder: (base) => ({
      ...base,
      color: "var(--textSecondary)",
      fontSize: "14px",
    }),
    input: (base) => ({
      ...base,
      color: "var(--textPrimary)",
      fontSize: "14px",
      margin: 0,
      padding: 0,
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: "14px",
    }),
    multiValue: (base) => ({
      ...base,
      alignItems: "center",
      backgroundColor: "var(--hoverSoft)",
      border: "1px solid var(--borderColor)",
      borderRadius: "5px",
      color: "var(--textPrimary)",
      display: "inline-flex",
      minHeight: "22px",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "var(--textPrimary)",
      fontSize: "14px",
      padding: "2px 6px",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "var(--textSecondary)",
      cursor: "pointer",
      padding: "0 6px",
      ":hover": {
        backgroundColor: "var(--borderColor)",
        color: "var(--textPrimary)",
      },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "var(--bgSurface)",
      border: "1px solid var(--borderColor)",
      borderRadius: "5px",
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.18)",
      overflow: "hidden",
      zIndex: 9999,
    }),
    menuList: (base) => ({
      ...base,
      padding: 0,
      backgroundColor: "var(--bgSurface)",
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? "var(--hoverSoft)" : "var(--bgSurface)",
      color: "var(--textPrimary)",
      fontSize: "14px",
      padding: "10px 12px",
      cursor: "pointer",
      minHeight: "42px",
    }),
    noOptionsMessage: (base) => ({
      ...base,
      color: "var(--textSecondary)",
      fontSize: "14px",
      padding: "10px 12px",
      backgroundColor: "var(--bgSurface)",
    }),
  };

  const renderQuestionSelect = (field, options, value, setter, afterChange) => (
    <select
      value={value || ""}
      onChange={(e) => {
        handleQuestionChange(field, e.target.value, setter);
        if (afterChange) afterChange(e.target.value);
      }}
      style={{
        width: "calc(100% - 10px)",
        height: "37px",
        marginTop: 0,
        padding: "8px",
        borderRadius: "5px",
      }}
    >
      <option value="">{t[language].selectOption}</option>

      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );

  const renderQuestionMultiSelect = (field, options, value, setter, afterChange) => {
    const selectOptions = toOptions(options);
    const selectedValues = parseMultiValue(value);

    return (
      <Select
        unstyled
        isMulti
        closeMenuOnSelect={false}
        components={{ DropdownIndicator: CustomDropdownIndicator }}
        value={selectOptions.filter((option) =>
          selectedValues.includes(option.value)
        )}
        onChange={(selectedOptions) => {
          handleMultiQuestionChange(field, selectedOptions, setter);
          if (afterChange) afterChange(selectedOptions || []);
        }}
        options={selectOptions}
        placeholder={t[language].selectMultiple}
        noOptionsMessage={() => t[language].noOptions}
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        menuPosition="fixed"
        styles={multiSelectStyles}
      />
    );
  };

  return (
    <>
      <div className="containerFlex">
        <div className="col-2">
          <label>
            <div>{t[language].phone} <b>*</b></div>
            <Tooltip text={t[language].phoneTooltip} />
          </label>

          <div className="phoneFieldGroup">
            <select
              value={countryIso}
              onChange={handleCountryChange}
              className="phonePrefixSelect"
            >
              {countries.map((country) => (
                <option key={`${country.iso}-${country.code}`} value={country.iso}>
                  {country.iso} ({country.code})
                </option>
              ))}
            </select>

            <input
              className="match phoneLocalInput"
              type="tel"
              name="contact_phone_local"
              value={phone || ""}
              onChange={handlePhoneChange}
              autoComplete="off"
              required
              pattern="^\d{6,14}$"
              title={t[language].phoneTitle}
            />
          </div>

          <FieldError error={formErrors.phone} />
        </div>

        <div className="col-2">
          <label>
            <div>{USA_MODE ? t[language].state : t[language].province} <b>*</b></div>
          </label>
          <select
            onChange={(e) => {
              setRegion(e.target.value);
              setStoredValue("region", e.target.value);
            }}
            value={region}
            required
          >
            <option value="" disabled>
              {USA_MODE ? t[language].selectState : t[language].selectProvince}
            </option>

            {(USA_MODE ? usaStates : paisesRegiones).map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>

          <FieldError error={formErrors.region} />
        </div>
      </div>

      <div className="containerFlex">
        <CompanySection
          language={language}
          company={company}
          companyOptions={companyOptions}
          handleCompanyChange={handleCompanyChange}
          createCompany={createCompany}
          formErrors={formErrors}
        />

        <div className="col-2 registrationQuestionField">
          <label>
            <div>{t[language].jobTitle} <b>*</b></div>
          </label>
          <input
            className="registrationQuestionInput"
            type="text"
            name="position"
            value={position || ""}
            onChange={(e) => {
              setPosition(e.target.value);
              setStoredValue("position", e.target.value);
            }}
            required
            placeholder={language === "EN" ? "Enter your job title" : "Ingresá tu cargo"}
          />
          <FieldError error={formErrors.position} />
        </div>
      </div>

      <div className="containerFlex">
        <div className="col-2">
          <label>
            <div>{t[language].industry} <b>*</b></div>
          </label>
          <select
            className="rubroSelectAligned"
            onChange={(e) => {
              setSector(e.target.value);
              setStoredValue("sector", e.target.value);
            }}
            value={sector}
            required
          >
            <option value="" disabled>
              {t[language].selectIndustry}
            </option>

            {sectorCompany.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>

          <FieldError error={formErrors.sector} />
        </div>
      </div>

      <div className="containerFlex registrationQuestionsGrid">
        <div className={`registrationQuestionField ${parseMultiValue(mainInterest).some(isOtherValue) ? "questionWithOther" : "questionSingle"}`}>
          <label>{t[language].mainInterest}</label>
          {renderQuestionMultiSelect(
            "main_interest",
            t[language].mainInterestOptions,
            mainInterest,
            setMainInterest,
            (selectedOptions) => {
              const selectedValues = selectedOptions.map((option) => option.value);
              if (!selectedValues.some(isOtherValue)) {
                setMainInterestOther("");
                setStoredValue("main_interest_other", "");
              }
            }
          )}
          {parseMultiValue(mainInterest).some(isOtherValue) && (
            <>
              <input
                className="registrationQuestionInput"
                type="text"
                value={mainInterestOther || ""}
                onChange={(e) => {
                  setMainInterestOther(e.target.value);
                  setStoredValue("main_interest_other", e.target.value);
                }}
                placeholder={t[language].specifyOther}
              />
              <FieldError error={formErrors.mainInterestOther} />
            </>
          )}
        </div>

        <div className={`registrationQuestionField ${isOtherValue(eventSource) ? "questionWithOther" : "questionSingle"}`}>
          <label>{t[language].eventSource}</label>
          {renderQuestionSelect(
            "event_source",
            t[language].eventSourceOptions,
            eventSource,
            setEventSource,
            (value) => {
              if (!isOtherValue(value)) {
                setEventSourceOther("");
                setStoredValue("event_source_other", "");
              }
            }
          )}
          {isOtherValue(eventSource) && (
            <>
              <input
                className="registrationQuestionInput"
                type="text"
                value={eventSourceOther || ""}
                onChange={(e) => {
                  setEventSourceOther(e.target.value);
                  setStoredValue("event_source_other", e.target.value);
                }}
                placeholder={t[language].specifyOther}
              />
              <FieldError error={formErrors.eventSourceOther} />
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default ContactInfoSection;

